import {
  Check,
  ChevronDownIcon,
  Files,
  Plus,
  SquareArrowOutDownRight,
} from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { LoadingSpinner } from "~/components/Icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { DOCLIST_EXPORT_LIST, DOCLIST_EXPORT_TYPE } from "../../constants";
import { DocListMode } from "../../types";
import { DocListFilter } from "./DocListFilter";

interface DocListActionsProps {
  mode: DocListMode;
  setMode: (mode: DocListMode) => void;
  isExportEmpty: boolean;
  isExporting: boolean;
  successfullyExport: boolean;
  onExport: (type: DOCLIST_EXPORT_TYPE) => void;
  onExportCancel: () => void;
  onAddCategory: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isViewOnly: boolean;
  onPossibleDuplicatesClick?: () => void;
  hasDuplicateSections?: boolean;
  hideDocxExport?: boolean;
}

export const DocListActions: FC<DocListActionsProps> = ({
  mode,
  setMode,
  isExportEmpty,
  isExporting,
  successfullyExport,
  onExport,
  onExportCancel,
  onAddCategory,
  searchTerm,
  setSearchTerm,
  isViewOnly,
  onPossibleDuplicatesClick,
  hasDuplicateSections,
  hideDocxExport,
}) => {
  const exportList = hideDocxExport
    ? DOCLIST_EXPORT_LIST.filter(
        (item) =>
          item.type !== DOCLIST_EXPORT_TYPE.DOCX &&
          item.type !== DOCLIST_EXPORT_TYPE.TOC_DOCX
      )
    : DOCLIST_EXPORT_LIST;
  const [isFilterFocused, setIsFilterFocused] = useState(false);

  if (mode === DocListMode.VIEW) {
    return (
      <div className="flex items-center gap-1 w-full">
        <DocListFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onFocus={() => setIsFilterFocused(true)}
          onBlur={() => setIsFilterFocused(false)}
        />
        {!isViewOnly && !isFilterFocused && (
          <div className="flex gap-1">
            {hasDuplicateSections && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  trackEvent({
                    eventName: "app/doc_list/duplicates.button/click",
                  });
                  onPossibleDuplicatesClick?.();
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                <Files className="w-4 h-4 mr-2" />
                <FormattedMessage
                  id="docList.duplicates.button"
                  defaultMessage="Duplicates"
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddCategory}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="hidden md:inline">
                <FormattedMessage id="docList.addCategory" />
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
              onClick={() => {
                trackEvent({
                  eventName: "app/doc_list/export_mode.button/click",
                });
                setMode(DocListMode.EXPORT);
              }}
            >
              {successfullyExport ? (
                <Check className="w-4 h-4 flex-shrink-0" />
              ) : (
                <SquareArrowOutDownRight className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="hidden md:inline">
                <FormattedMessage id="common.export" />
              </span>
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (mode === DocListMode.EXPORT) {
    return (
      <div className="flex items-center gap-6 w-full">
        <DocListFilter searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onExportCancel}
            className="text-blue-600 hover:text-blue-700"
          >
            <span className="hidden md:inline">
              <FormattedMessage id="cancel" />
            </span>
          </Button>
          <div className="inline-flex rounded-md overflow-hidden">
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2 rounded-r-none"
              disabled={isExportEmpty || isExporting}
              onClick={() => {
                if (isExportEmpty || isExporting) return;
                const defaultType = exportList[0]?.type;
                trackEvent({
                  eventName: "app/doc_list/export_dropdown/click",
                  properties: { type: defaultType },
                });
                onExport(defaultType);
              }}
            >
              {isExporting ? (
                <>
                  <LoadingSpinner className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden md:inline">
                    <FormattedMessage id="common.exporting" />
                  </span>
                </>
              ) : (
                <>
                  <SquareArrowOutDownRight className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">
                    <FormattedMessage id="common.export" />
                  </span>
                </>
              )}
            </Button>

            <div
              aria-hidden="true"
              className="w-px h-8 self-stretch bg-white/10"
            />

            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                disabled={isExportEmpty || isExporting}
              >
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 rounded-l-none px-2"
                  aria-label="More export options"
                >
                  <ChevronDownIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  {exportList.map((item) => (
                    <DropdownMenuItem
                      key={item.label}
                      onClick={() => {
                        trackEvent({
                          eventName: "app/doc_list/export_dropdown/click",
                          properties: { type: item.type },
                        });
                        onExport(item.type);
                      }}
                    >
                      <FormattedMessage id={item.label} />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
